// Media Library — inline line-art icons (WL-023).
// Line-art only, never emoji (design law). currentColor-driven so callers theme
// them via `color`. Each is a thin functional wrapper around an <svg>.
import React from "react";
import CaptureFrame from "../../assets/icons/CaptureFrame";

const svg = (size, children, extra = {}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...extra}
  >
    {children}
  </svg>
);

export const IconImage = ({ size = 18 }) =>
  svg(size, (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9" r="1.6" />
      <path d="M4 17l5-5 4 4 3-3 4 4" />
    </>
  ));

export const IconVideo = ({ size = 18 }) =>
  svg(size, (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none" />
    </>
  ));

export const IconAudio = ({ size = 18 }) =>
  svg(size, (
    <>
      <path d="M9 18V6l10-2v12" />
      <circle cx="6.5" cy="18" r="2.5" />
      <circle cx="16.5" cy="16" r="2.5" />
    </>
  ));

export const IconDocument = ({ size = 18 }) =>
  svg(size, (
    <>
      <path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v4h4" />
      <path d="M8 13h8M8 17h6" />
    </>
  ));

// G-168: Capture glyph — reuses the SAME header-nav Capture icon component
// (assets/icons/CaptureFrame: viewfinder corner-brackets + center "+") rather
// than a separate document/sticky-note glyph, so every Capture-labelled
// surface (header nav entry, Media Library "Captures" tab, capture-badge
// pill) renders the identical icon component. `size` maps to CaptureFrame's
// width/height so this drop-in keeps the same call signature every other
// icon in this module uses (`<IconCapture size={n} />`).
export const IconCapture = ({ size = 18 }) => (
  <CaptureFrame width={size} height={size} />
);

export const IconAllTiles = ({ size = 18 }) =>
  svg(size, (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    </>
  ));

export const IconGridLarge = ({ size = 18 }) =>
  svg(size, (
    <>
      <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13" y="3.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="3.5" y="13" width="7.5" height="7.5" rx="1.5" />
      <rect x="13" y="13" width="7.5" height="7.5" rx="1.5" />
    </>
  ));

export const IconGridSmall = ({ size = 18 }) =>
  svg(size, (
    <>
      <rect x="3.5" y="3.5" width="4.6" height="4.6" rx="1" />
      <rect x="9.7" y="3.5" width="4.6" height="4.6" rx="1" />
      <rect x="15.9" y="3.5" width="4.6" height="4.6" rx="1" />
      <rect x="3.5" y="9.7" width="4.6" height="4.6" rx="1" />
      <rect x="9.7" y="9.7" width="4.6" height="4.6" rx="1" />
      <rect x="15.9" y="9.7" width="4.6" height="4.6" rx="1" />
      <rect x="3.5" y="15.9" width="4.6" height="4.6" rx="1" />
      <rect x="9.7" y="15.9" width="4.6" height="4.6" rx="1" />
      <rect x="15.9" y="15.9" width="4.6" height="4.6" rx="1" />
    </>
  ));

export const IconList = ({ size = 18 }) =>
  svg(size, (
    <>
      <path d="M8 6h12M8 12h12M8 18h12" />
      <circle cx="4" cy="6" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4" cy="18" r="1.2" fill="currentColor" stroke="none" />
    </>
  ));

export const IconFolder = ({ size = 24 }) =>
  svg(size, (
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  ));

export const IconSearch = ({ size = 18 }) =>
  svg(size, (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M20 20l-4.5-4.5" />
    </>
  ));

export const IconClose = ({ size = 20 }) =>
  svg(size, <path d="M6 6l12 12M18 6L6 18" />);

export const IconEdit = ({ size = 18 }) =>
  svg(size, (
    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  ));

export const IconKebab = ({ size = 18 }) =>
  svg(size, (
    <>
      <circle cx="12" cy="5" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1.3" fill="currentColor" stroke="none" />
    </>
  ));

export const IconPlus = ({ size = 20 }) =>
  svg(size, <path d="M12 5v14M5 12h14" />);

export const IconTrash = ({ size = 18, ...extra }) =>
  svg(size, (
    <>
      <path d="M4 7h16" />
      <path d="M9 7V4h6v3" />
      <path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
      <path d="M10 11v6M14 11v6" />
    </>
  ), extra);

export const IconChevronDown = ({ size = 18 }) =>
  svg(size, <path d="M6 9l6 6 6-6" />);

export const IconChevronLeft = ({ size = 18 }) =>
  svg(size, <path d="M15 6l-6 6 6 6" />);

export const IconChevronRight = ({ size = 18 }) =>
  svg(size, <path d="M9 6l6 6-6 6" />);

// Two opposed vertical arrows — a "cycle sort order" glyph (NOT a dropdown
// chevron). EW-1284: the standalone Media Library sort control click-cycles
// Newest→Oldest→Name→Size; a ChevronDown falsely implied a dropdown menu.
export const IconSort = ({ size = 18 }) =>
  svg(size, (
    <>
      <path d="M8 4v15M8 4L5 7M8 4l3 3" />
      <path d="M16 20V5M16 20l-3-3M16 20l3-3" />
    </>
  ));

export const IconCheck = ({ size = 16 }) =>
  svg(size, <path d="M5 12l4 4 10-10" />);

export const IconTarget = ({ size = 16 }) =>
  svg(size, (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
    </>
  ));

export const IconUpload = ({ size = 18 }) =>
  svg(size, (
    <>
      <path d="M12 15V4" />
      <path d="M7 9l5-5 5 5" />
      <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
    </>
  ));

// Line-art "i" in a circle — the ineligible-item info affordance (DL-048 modal).
export const IconInfo = ({ size = 16 }) =>
  svg(size, (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <circle cx="12" cy="7.75" r="0.9" fill="currentColor" stroke="none" />
    </>
  ));

// Line-art stacked-tiles glyph — the "Reuse from your Media Library" affordance.
export const IconLibrary = ({ size = 18 }) =>
  svg(size, (
    <>
      <rect x="3" y="7" width="14" height="12" rx="2" />
      <path d="M7 4h11a2 2 0 0 1 2 2v9" />
      <path d="M7 12l2.5-2.5L13 13" />
    </>
  ));

// Glyph + brand color per media type (deck: image #29C18C, video #9B5AF8,
// audio #F472B6, document #F79A00). Line-art only.
export const TYPE_GLYPH = {
  image: { Comp: IconImage, color: "#29C18C" },
  video: { Comp: IconVideo, color: "#9B5AF8" },
  audio: { Comp: IconAudio, color: "#F472B6" },
  document: { Comp: IconDocument, color: "#F79A00" },
};
